import { useState, createContext, useContext, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import useCollections from '../Hooks/useCollections'
import useTarifs from '../Hooks/useTarifs'
import useTranslation from '../Hooks/useTranslation'
import type { MinMaxPrice } from '../types'

/** Item de filtre générique (gamme, collection, famille, etc.) */
interface FilterItem {
   id: string
   label: string
   visible: boolean
   checked: boolean
}

const theContext = createContext<any>(undefined)

const CatalogFilterContext = () => useContext(theContext)

const CatalogFilterContextProvider = ({ children }: { children: ReactNode }) => {
   const {
      getGammesAsSortedArray,
      getCollectionsAsSortedArray,
      getSousCollectionsAsSortedArray,
      getUniversAsSortedArray,
      getGammeForCollectionId,
      getFamillesAsSortedArray,
      getPortesAsSortedArray,
      getCouleursAsSortedArray,
      getCyclesAsSortedArray,
      getAllModelsAsArray
   } = useCollections()
   const { getMinMaxPriceForEachModelInArray } = useTarifs()
   const [paramCollectionId, setParamCollectionId] = useState<string | null>(null)
   const [paramSousCollectionId, setParamSousCollectionId] = useState<string | null>(null)
   const [paramGammeId, setParamGammeId] = useState<string | null>(null)
   const [minPrice, setMinPrice] = useState<number | null>(null)
   const [maxPrice, setMaxPrice] = useState<number | null>(null)
   const [gammes, setGammes] = useState<FilterItem[] | null>(null)
   const [collections, setCollections] = useState<FilterItem[] | null>(null)
   const [sousCollections, setSousCollections] = useState<FilterItem[] | null>(null)
   const [familles, setFamilles] = useState<FilterItem[] | null>(null)
   const [portes, setPortes] = useState<FilterItem[] | null>(null)
   const [couleurs, setCouleurs] = useState<FilterItem[] | null>(null)
   const [univers, setUnivers] = useState<FilterItem[] | null>(null)
   const [paramComm, setParamComm] = useState<FilterItem[] | null>(null)
   const [cycles, setCycles] = useState<FilterItem[] | null>(null)
   const [excluBoutique, setExcluBoutique] = useState<string | null>(null)
   const [boMonoPaire, setBOMonoPaire] = useState<string | null>(null)
   const [doFilter, setDoFilter] = useState(false)
   const [minManualPrice, setMinManualPrice] = useState<number | null>(null)
   const [maxManualPrice, setMaxManualPrice] = useState<number | null>(null)
   const [allMinMax, setAllMinMax] = useState<Record<string, MinMaxPrice> | null>(null)
   const [searchText, setSearchText] = useState('')
   const [suspendRecalc, setSuspendRecalc] = useState(true)
   const { trans } = useTranslation()

   useEffect(() => {
      const gamme_id = paramGammeId ?? (paramCollectionId ? getGammeForCollectionId(paramCollectionId)?.id : null)

      console.log('+++GAMMES', gamme_id, paramCollectionId)
      setGammes(
         getGammesAsSortedArray().map((gamme) => {
            return {
               id: gamme.id,
               label: gamme.label,
               visible: true,
               checked: gamme_id === gamme.id
            }
         })
      )
   }, [getGammeForCollectionId, getGammesAsSortedArray, paramCollectionId, paramGammeId])

   useEffect(() => {
      setCollections(
         getCollectionsAsSortedArray().map((collection) => {
            return {
               id: collection.id,
               label: collection.label,
               visible: true,
               checked: paramCollectionId === collection.id
            }
         })
      )
   }, [getCollectionsAsSortedArray, paramCollectionId])

   useEffect(() => {
      setSousCollections(
         getSousCollectionsAsSortedArray().map((collection) => {
            return {
               id: collection.id,
               label: collection.label,
               visible: true,
               checked: false
            }
         })
      )
   }, [getSousCollectionsAsSortedArray])

   useEffect(() => {
      setUnivers(
         getUniversAsSortedArray().map((univers) => {
            return {
               id: univers.id,
               label: univers.label,
               visible: true,
               checked: false
            }
         })
      )
   }, [getUniversAsSortedArray])

   useEffect(() => {
      // En dur un peu cracra mais je n'ai pas de WS qui me fournit ces info...
      setParamComm((cur: FilterItem[] | null) => {
         if (!cur) {
            return [
               {
                  id: 'best',
                  label: trans('?BEST SELLER'),
                  visible: true,
                  checked: false
               },
               {
                  id: 'warn',
                  label: trans('?WARNING'),
                  visible: true,
                  checked: false
               },
               {
                  id: 'iconic',
                  label: trans('?ICONIC'),
                  visible: true,
                  checked: false
               },
               {
                  id: 'spo',
                  label: trans('?SPO'),
                  visible: true,
                  checked: false
               },
               {
                  id: 'surco',
                  label: trans('?SURCO'),
                  visible: true,
                  checked: false
               }
            ]
         } else {
            return cur
         }
      })
   }, [trans])

   useEffect(() => {
      setFamilles(
         getFamillesAsSortedArray().map((famille) => {
            return {
               id: famille.id,
               label: famille.label,
               visible: true,
               checked: false
            }
         })
      )
   }, [getFamillesAsSortedArray])

   useEffect(() => {
      setPortes(
         getPortesAsSortedArray().map((porte) => {
            return {
               id: porte.id,
               label: porte.label,
               visible: true,
               checked: false
            }
         })
      )
   }, [getPortesAsSortedArray])

   useEffect(() => {
      console.log('*** ICI setCouleurs2')

      setCouleurs(
         getCouleursAsSortedArray().map((couleur) => {
            return {
               id: couleur.id,
               label: couleur.label,
               visible: true,
               checked: false
            }
         })
      )
   }, [getCouleursAsSortedArray, setCouleurs])

   useEffect(() => {
      setCycles(
         getCyclesAsSortedArray().map((cycle) => {
            return {
               id: cycle.id,
               label: cycle.label,
               visible: true,
               checked: false
            }
         })
      )
   }, [getCyclesAsSortedArray, setCycles])

   /*******
    ******* RÉCUPÉRATION DES ITEMS SÉLECTIONNÉS (GAMMES, COLLECTIONS, FAMILLES, PORTES, COULEURS)
    *******/

   const getSelectedGammes = useCallback(() => {
      return gammes?.filter((gamme) => gamme.checked)
   }, [gammes])

   const getSelectedGammesIds = useCallback((): string[] => {
      const _gammes = getSelectedGammes()
      return _gammes?.map((gamme) => gamme.id) ?? []
   }, [getSelectedGammes])

   const getSelectedCollections = useCallback(() => {
      return collections?.filter((collection) => collection.checked && collection.visible)
   }, [collections])

   const getSelectedCollectionsIds = useCallback((): string[] => {
      const _collections = getSelectedCollections()
      return _collections?.map((collection) => collection.id) ?? []
   }, [getSelectedCollections])

   const getSelectedSousCollections = useCallback(() => {
      return sousCollections?.filter((collection) => collection.checked && collection.visible)
   }, [sousCollections])

   const getSelectedSousCollectionsIds = useCallback((): string[] => {
      const _collections = getSelectedSousCollections()
      return _collections?.map((collection) => collection.id) ?? []
   }, [getSelectedSousCollections])

   const getSelectedFamilles = useCallback(() => {
      return familles?.filter((famille) => famille.checked && famille.visible)
   }, [familles])

   const getSelectedFamillesIds = useCallback((): string[] => {
      const _familles = getSelectedFamilles()
      return _familles?.map((famille) => famille.id) ?? []
   }, [getSelectedFamilles])

   const getSelectedPortes = useCallback(() => {
      return portes?.filter((porte) => porte.checked && porte.visible)
   }, [portes])

   const getSelectedPortesIds = useCallback((): string[] => {
      const _portes = getSelectedPortes()
      return _portes?.map((porte) => porte.id) ?? []
   }, [getSelectedPortes])

   const getSelectedCouleurs = useCallback(() => {
      return couleurs?.filter((couleur) => couleur.checked && couleur.visible)
   }, [couleurs])

   const getSelectedCouleursIds = useCallback((): string[] => {
      const _couleurs = getSelectedCouleurs()
      return _couleurs?.map((couleur) => couleur.id) ?? []
   }, [getSelectedCouleurs])

   const getSelectedCycles = useCallback(() => {
      return cycles?.filter((cycle) => cycle.checked && cycle.visible)
   }, [cycles])

   const getSelectedCyclesIds = useCallback((): string[] => {
      const _cycles = getSelectedCycles()
      return _cycles?.map((cycle) => cycle.id) ?? []
   }, [getSelectedCycles])

   const getSelectedUnivers = useCallback(() => {
      return univers?.filter((univ) => univ.checked && univ.visible)
   }, [univers])

   const getSelectedUniversIds = useCallback((): string[] => {
      const _univers = getSelectedUnivers()
      return _univers?.map((univ) => univ.id) ?? []
   }, [getSelectedUnivers])

   const getSelectedParamComm = useCallback(() => {
      return paramComm?.filter((param) => param.checked && param.visible)
   }, [paramComm])

   const getSelectedParamCommIds = useCallback((): string[] => {
      const param = getSelectedParamComm()
      return param?.map((param) => param.id) ?? []
   }, [getSelectedParamComm])

   const clearGammes = useCallback(() => {
      console.log('ON GAMME CLEAR')
      const arr = gammes!.map((gamme) => {
         return { ...gamme, checked: false }
      })
      setGammes(arr)
   }, [gammes])

   const clearCollections = useCallback(() => {
      const arr = collections!.map((collection) => {
         return { ...collection, checked: false }
      })
      setCollections(arr)
   }, [collections])

   const clearSousCollections = useCallback(() => {
      const arr = sousCollections!.map((collection) => {
         return { ...collection, checked: false }
      })
      setSousCollections(arr)
   }, [sousCollections])

   const clearFamilles = useCallback(() => {
      const arr = familles!.map((famille) => {
         return { ...famille, checked: false }
      })
      setFamilles(arr)
   }, [familles])

   const clearPortes = useCallback(() => {
      const arr = portes!.map((porte) => {
         return { ...porte, checked: false }
      })
      setPortes(arr)
   }, [portes])

   const clearCouleurs = useCallback(() => {
      console.log('*** ICI clearCouleurs')
      const arr = couleurs!.map((couleur) => {
         return { ...couleur, checked: false }
      })
      setCouleurs(arr)
   }, [couleurs])

   const clearCycles = useCallback(() => {
      const arr = cycles!.map((cycle) => {
         return { ...cycle, checked: false }
      })
      setCycles(arr)
   }, [cycles])

   const clearUnivers = useCallback(() => {
      const arr = univers!.map((univ) => {
         return { ...univ, checked: false }
      })
      setUnivers(arr)
   }, [univers])

   const clearParamComm = useCallback(() => {
      const arr = paramComm!.map((param) => {
         return { ...param, checked: false }
      })
      setParamComm(arr)
   }, [paramComm])

   const clearExcluBoutique = useCallback(() => {
      setExcluBoutique(null)
   }, [])

   const clearAllFilters = useCallback(() => {
      clearGammes()
      clearCollections()
      clearSousCollections()
      clearFamilles()
      clearPortes()
      clearCouleurs()
      clearUnivers()
      clearParamComm()
      clearCycles()
      clearExcluBoutique()
      setMinManualPrice(null)
      setMaxManualPrice(null)
   }, [clearCollections, clearSousCollections, clearCouleurs, clearCycles, clearUnivers, clearParamComm, clearExcluBoutique, clearFamilles, clearGammes, clearPortes])

   useEffect(() => {
      if (!suspendRecalc) {
         const ret = getMinMaxPriceForEachModelInArray(getAllModelsAsArray())
         setAllMinMax(ret)
      }
   }, [getAllModelsAsArray, getMinMaxPriceForEachModelInArray, suspendRecalc])

   return (
      <theContext.Provider
         value={{
            doFilter,
            setDoFilter,
            searchText,
            setSearchText,
            paramCollectionId,
            setParamCollectionId,
            paramSousCollectionId,
            setParamSousCollectionId,
            paramGammeId,
            setParamGammeId,
            allMinMax,
            setAllMinMax,
            minPrice,
            maxPrice,
            setMaxPrice,
            setMinPrice,
            gammes,
            setGammes,
            collections,
            setCollections,
            sousCollections,
            setSousCollections,
            univers,
            setUnivers,
            paramComm,
            setParamComm,
            familles,
            setFamilles,
            portes,
            setPortes,
            couleurs,
            setCouleurs,
            cycles,
            setCycles,
            boMonoPaire,
            setBOMonoPaire,
            excluBoutique,
            setExcluBoutique,
            minManualPrice,
            setMinManualPrice,
            maxManualPrice,
            setMaxManualPrice,
            getSelectedGammes,
            getSelectedGammesIds,
            getSelectedCollections,
            getSelectedCollectionsIds,
            getSelectedSousCollections,
            getSelectedSousCollectionsIds,
            getSelectedFamilles,
            getSelectedFamillesIds,
            getSelectedPortes,
            getSelectedPortesIds,
            getSelectedCouleurs,
            getSelectedCouleursIds,
            getSelectedCyclesIds,
            getSelectedUnivers,
            getSelectedUniversIds,
            getSelectedParamComm,
            getSelectedParamCommIds,
            clearGammes,
            clearCollections,
            clearSousCollections,
            clearFamilles,
            clearPortes,
            clearCouleurs,
            clearCycles,
            clearUnivers,
            clearParamComm,
            clearExcluBoutique,
            clearAllFilters,
            suspendRecalc,
            setSuspendRecalc
         }}
      >
         {children}
      </theContext.Provider>
   )
}

export { CatalogFilterContext, CatalogFilterContextProvider }
export type { FilterItem }
